import { GraphQLClient } from 'graphql-request'
import { MutationCache, QueryCache, QueryClient } from 'react-query'
import { getAuth } from '../app/modules/auth/core/AuthHelpers'
import { refetchAccessToken } from '../api/authentication'
import { investorApi } from '../api/investorApi'

const jwt = getAuth()
const requestHeaders = {
  authorization: jwt!,
}

export function getApiErrorMessage(error: any) {
  return (
    error?.response?.errors?.[0]?.message ||
    error?.response?.data?.errorMessage ||
    error?.response?.data?.message ||
    error?.message ||
    'Something went wrong. Please try again.'
  )
}

export function isNoInvestorFoundError(error: any) {
  const message = getApiErrorMessage(error)
  return (
    /investor/i.test(message) &&
    /(not found|no investor|not associated|not mapped|does not have|doesn't have)/i.test(message)
  )
}

export function isUnauthorizedError(error: any) {
  const graphqlError = error?.response?.errors?.[0]
  const status =
    error?.response?.status ??
    error?.status ??
    error?.response?.data?.status ??
    error?.response?.data?.statusCode ??
    graphqlError?.extensions?.status ??
    graphqlError?.extensions?.statusCode ??
    graphqlError?.extensions?.response?.status ??
    graphqlError?.extensions?.response?.statusCode
  const code = graphqlError?.extensions?.code ?? error?.extensions?.code

  return (
    Number(status) === 401 ||
    code === 'UNAUTHENTICATED' ||
    code === 'UNAUTHORIZED' ||
    code === 'AUTHENTICATION_ERROR'
  )
}

export const graphqlRequestClient = new GraphQLClient(process.env.REACT_APP_GQL_URL as string, {
  headers: requestHeaders,
})

export const queryCache = new QueryCache({
  onError: async (error, query) => {
    const queryError = error as any

    if (isUnauthorizedError(queryError)) {
      console.log('Token expired, refreshing...')

      try {
        // Fetch new token
        const { data: newToken } = await refetchAccessToken()
        sessionStorage.setItem('accessToken', newToken)
        graphqlRequestClient.setHeader('authorization', `${newToken}`)
        investorApi.defaults.headers.common['Authorization'] = `${newToken}`

        // Retry the failed query
        return query.fetch()
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
      }
    }
  },
})

const mutationCache = new MutationCache({
  onError: async (error, variables, context, mutation) => {
    const mutationError = error as any

    if (isUnauthorizedError(mutationError) && !isNoInvestorFoundError(mutationError)) {
      console.log('Token expired, refreshing....')

      try {
        // Fetch new token
        const { data: newToken } = await refetchAccessToken()
        sessionStorage.setItem('accessToken', newToken)
        graphqlRequestClient.setHeader('authorization', `${newToken}`)
        investorApi.defaults.headers.common['Authorization'] = `${newToken}`

        // Retry the failed mutation
        return mutation.execute()
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
      }
    }

    // Don't call the original mutation's onError handler here
    // React Query will handle this automatically, preventing duplicate error handling
  },
})

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 30,
      cacheTime: 1000 * 60 * 10,
    },
  },
  mutationCache,
  queryCache,
})
